import { useState, useEffect, useCallback } from "react";
import {
  Page,
  Card,
  BlockStack,
  Text,
  Button,
  Banner,
  ProgressBar,
  InlineStack,
  Box,
  Spinner,
} from "@shopify/polaris";
import { useApi } from "../hooks/useApi";
import { createOnboardingApi } from "../api/onboarding";
import ContractForm, {
  ContractFormData,
  defaultContractFormData,
} from "../components/ContractForm";
import { useTranslation } from "../hooks/useTranslation";
import OnboardingHelpCard from "../components/OnboardingHelpCard";

type OnboardingStatus = "pending_form" | "pending_signing" | "completed";

interface OnboardingPageProps {
  onComplete?: () => void;
  skipInitialCheck?: boolean;
}

export default function OnboardingPage({ onComplete, skipInitialCheck = false }: OnboardingPageProps) {
  const { t } = useTranslation();
  const { Call } = useApi({ baseUrl: "/api" });
  const onboardingApi = createOnboardingApi(Call);

  const [step, setStep] = useState<1 | 2>(1);
  const [contractSigned, setContractSigned] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [formData, setFormData] = useState<ContractFormData>(
    defaultContractFormData,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [signingUrl, setSigningUrl] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(!skipInitialCheck);
  const [apiError, setApiError] = useState<string | null>(null);

  // Shown once, driven by the backend (`onboarding_welcome_shown_at`), not a
  // client-side ?installed=true param: Shopify Admin does not forward custom
  // query params into the embedded app's iframe URL, so that never reached
  // window.location here on a real install.
  const markWelcomeShown = useCallback(() => {
    setShowWelcome(true);
    // Fire-and-forget: even if this fails, the next status check will just
    // show the welcome card again, which is harmless.
    onboardingApi.markWelcomeShown().catch(() => {});
  }, [onboardingApi]);

  // On mount, check current onboarding status to resume where left off
  useEffect(() => {
    if (skipInitialCheck) return; // status already resolved by App.tsx — skip duplicate fetch
    const checkStatus = async (attempt = 0) => {
      // Set when this call defers to a later step (retry or an OAuth
      // install redirect already in flight) rather than reaching a final
      // status, so the loading spinner stays up instead of flashing the
      // welcome/onboarding UI right before the browser navigates away.
      let isTransient = false;
      try {
        const response = await onboardingApi.getStatus();
        if (response.needs_installation) {
          isTransient = true;
          return;
        }
        if (response.success && response.data) {
          const status: OnboardingStatus = response.data.onboarding_status;
          if (status === "pending_signing") {
            setStep(2);
            // Re-fetch the signing URL so the sign button is available on reload
            try {
              const urlResp = await onboardingApi.getSigningUrl();
              if (urlResp.success && urlResp.data?.signing_url) {
                setSigningUrl(urlResp.data.signing_url);
              }
            } catch {
              // Non-fatal — falls back to no-URL (pending) state
            }
          } else if (status === "completed") {
            onComplete?.();
          } else if (response.data.show_welcome) {
            markWelcomeShown();
          }
        } else if (response.error === "Authentication required" && attempt < 2) {
          isTransient = true;
          setTimeout(() => checkStatus(attempt + 1), 1500);
          return;
        }
      } catch (e) {
        // First visit / network error, stay on step 1
      } finally {
        if (!isTransient) {
          setStatusLoading(false);
        }
      }
    };
    checkStatus();
  }, []);

  // Poll /onboarding/status every 5 seconds while on step 2.
  // When the SignWell webhook fires and sets contract_signed_at the backend
  // returns contract_signed: true — we show a brief confirmation and redirect.
  useEffect(() => {
    if (step !== 2 || contractSigned) return;
    const id = setInterval(async () => {
      try {
        const res = await onboardingApi.getStatus();
        if (res.success && res.data?.contract_signed) {
          clearInterval(id);
          setContractSigned(true);
          // Give the user a moment to read the confirmation, then redirect.
          setTimeout(() => handleSigningComplete(), 2500);
        }
      } catch {
        // Non-fatal — keep polling
      }
    }, 5000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, contractSigned]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.company_official_name.trim()) {
      newErrors.company_official_name = t("validation.required");
    }
    if (!formData.company_short_name.trim()) {
      newErrors.company_short_name = t("validation.required");
    }
    if (!formData.issuer_vat_number.trim() || formData.issuer_vat_number.length !== 9) {
      newErrors.issuer_vat_number = t("validation.vatLength");
    }
    if (!formData.tax_office.trim()) {
      newErrors.tax_office = t("validation.required");
    }
    if (!formData.profession.trim()) {
      newErrors.profession = t("validation.required");
    }
    if (!formData.issuer_address.trim()) {
      newErrors.issuer_address = t("validation.required");
    }
    if (!formData.issuer_city.trim()) {
      newErrors.issuer_city = t("validation.required");
    }
    if (!formData.issuer_postal_code.trim()) {
      newErrors.issuer_postal_code = t("validation.required");
    }
    if (!formData.legal_representative_name.trim()) {
      newErrors.legal_representative_name = t("validation.required");
    }
    if (
      !formData.administrator_personal_vat_id.trim() ||
      formData.administrator_personal_vat_id.length !== 9
    ) {
      newErrors.administrator_personal_vat_id = t("validation.vatLength");
    }
    if (!formData.contact_email.trim() || !formData.contact_email.includes("@")) {
      newErrors.contact_email = t("validation.emailInvalid");
    }

    // Alt provider validation for B2C
    const needsAlt =
      formData.service_type === "b2c" || formData.service_type === "b2b_and_b2c";
    if (needsAlt) {
      if (!formData.alt_provider_name.trim()) {
        newErrors.alt_provider_name = t("contractForm.fields.requiredB2c");
      }
      if (!formData.alt_provider_contract_nr.trim()) {
        newErrors.alt_provider_contract_nr = t("contractForm.fields.requiredB2c");
      }
      if (!formData.alt_provider_contract_date.trim()) {
        newErrors.alt_provider_contract_date = t("contractForm.fields.requiredB2c");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const handleSubmitForm = useCallback(async () => {
    if (!validateForm()) return;

    setLoading(true);
    setApiError(null);

    try {
      const response = await onboardingApi.submit(formData);
      if (response.success && response.data) {
        setSigningUrl(response.data.signing_url);
        setStep(2);
      } else {
        setApiError(response.error || t("onboarding.errors.submitFailed"));
      }
    } catch (e: any) {
      setApiError(e.message || t("onboarding.errors.networkError"));
    } finally {
      setLoading(false);
    }
  }, [formData, validateForm, t]);

  const handleSigningComplete = useCallback(async () => {
    setLoading(true);
    setApiError(null);

    try {
      const response = await onboardingApi.complete();
      if (response.success) {
        // Finishing the wizard does NOT assert the contract is signed. Real
        // signing status is tracked separately (SignWell webhook / admin) and
        // surfaced on the dashboard. Just proceed to the dashboard.
        onComplete?.();
      } else if (response.error === "Authentication required") {
        // App Bridge token expired while user was on SignWell tab — reload to refresh
        setApiError(t("onboarding.errors.sessionExpired"));
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setApiError(response.error || t("onboarding.errors.completeFailed"));
      }
    } catch (e: any) {
      setApiError(e.message || t("onboarding.errors.networkError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  if (statusLoading) {
    return (
      <Page>
        <Card>
          <BlockStack gap="400" inlineAlign="center">
            <Spinner size="large" />
            <Text as="p" alignment="center">
              {t("onboarding.loading")}
            </Text>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  const progressValue = step === 1 ? 50 : 100;

  if (showWelcome) {
    return (
      <Page>
        <BlockStack gap="400">
          <Card>
            <BlockStack gap="500">
              <BlockStack gap="200">
                <Text as="h2" variant="headingLg">
                  {t("onboarding.welcome.title")}
                </Text>
                <Text as="p" variant="bodyLg" tone="subdued">
                  {t("onboarding.welcome.subtitle")}
                </Text>
              </BlockStack>

              <Banner tone="info">
                <p>
                  {t("onboarding.welcome.durationText", { duration: t("onboarding.welcome.durationBold") })}
                </p>
              </Banner>

              <InlineStack align="end">
                <Button
                  variant="primary"
                  size="large"
                  onClick={() => setShowWelcome(false)}
                >
                  {t("onboarding.welcome.startButton")}
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
          <OnboardingHelpCard />
        </BlockStack>
      </Page>
    );
  }

  return (
    <Page>
      <BlockStack gap="400">
        {/* Progress indicator */}
        <Card>
          <BlockStack gap="200">
            <InlineStack align="space-between">
              <Text as="h2" variant="headingLg">
                {t("onboarding.title")}
              </Text>
              <Text as="span" variant="bodySm" tone="subdued">
                {t("onboarding.step", { step: String(step) })}
              </Text>
            </InlineStack>
            <ProgressBar progress={progressValue} size="small" />
          </BlockStack>
        </Card>

        {apiError && (
          <Banner tone="critical" onDismiss={() => setApiError(null)}>
            <p>{apiError}</p>
          </Banner>
        )}

        {/* Step 1: Form */}
        {step === 1 && (
          <Card>
            <BlockStack gap="400">
              <Banner tone="info">
                <p>{t("onboarding.form.banner")}</p>
              </Banner>

              <ContractForm
                data={formData}
                onChange={setFormData}
                errors={errors}
                disabled={loading}
              />

              <InlineStack align="end">
                <Button
                  variant="primary"
                  onClick={handleSubmitForm}
                  loading={loading}
                >
                  {t("onboarding.form.continueButton")}
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        )}

        {/* Step 2: Sign contract — two states:
            A) signingUrl present  → user must sign (unsigned document)
            B) signingUrl absent   → contract pending/already signed (check email) */}
        {step === 2 && (
          <Card>
            <BlockStack gap="500">
              {/* Signed state — shown briefly before auto-redirect */}
              {contractSigned ? (
                <BlockStack gap="300" inlineAlign="center">
                  <Text as="h2" variant="headingLg" alignment="center">
                    {t("onboarding.signing.signedTitle")}
                  </Text>
                  <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                    {t("onboarding.signing.signedDescription")}
                  </Text>
                </BlockStack>
              ) : (
              <>
              <BlockStack gap="200">
                <Text as="h2" variant="headingLg">
                  {signingUrl
                    ? t("onboarding.signing.title")
                    : t("onboarding.signing.pendingTitle")}
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued">
                  {signingUrl
                    ? t("onboarding.signing.description")
                    : t("onboarding.signing.pendingDescription")}
                </Text>
              </BlockStack>

              {signingUrl ? (
                <>
                  <Box padding="500" background="bg-surface-secondary" borderRadius="200">
                    <BlockStack gap="300" inlineAlign="center">
                      <Button
                        variant="primary"
                        url={signingUrl}
                        external
                        size="large"
                      >
                        {t("onboarding.signing.signButton")}
                      </Button>
                      <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                        {t("onboarding.signing.openNote")}
                      </Text>
                    </BlockStack>
                  </Box>

                  <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                    {t("onboarding.signing.activationNote")}
                  </Text>

                  <InlineStack align="center">
                    <Button
                      variant="plain"
                      onClick={handleSigningComplete}
                      loading={loading}
                    >
                      {t("onboarding.signing.skipButton")}
                    </Button>
                  </InlineStack>
                </>
              ) : (
                <Banner tone="success">
                  <p>{t("onboarding.signing.activationNote")}</p>
                </Banner>
              )}
              </>
              )}
            </BlockStack>
          </Card>
        )}

        {/* Step 2 is the final wizard step; contract signing status lives on
            the dashboard (ContractStatusCard). */}

        <OnboardingHelpCard />
      </BlockStack>
    </Page>
  );
}
